import { hasDriver, type VehicleInput } from "@szerviz/core";
import { useTranslation } from "react-i18next";
import { normalizePhone } from "../../../../supabase/functions/_shared/messaging";
import { useFeature } from "../context";
import type { Customer, Vehicle } from "../data";
import { CustomerContactLinks } from "./ContactLinks";
import { TextInput } from "./Field";
import { Help } from "./Help";

/**
 * "Sofőr, aki használja az autót" (feature "vehicleDriver", owner, 2026-11-05): a pastel section of the vehicle form.
 * The stored driver's phone first as big Call / SMS / e-mail buttons, then the three fields, and the line that says
 * what the driver gets (the ready notice and the intake information) and what never (quotes, prices, invoices, reminders).
 */
export function DriverFields({ form, stored, onChange, bad }: {
  form: VehicleInput;
  /** The saved vehicle (its contacts are the buttons); none for a new vehicle. */
  stored?: VehicleInput | null;
  onChange: (patch: Partial<VehicleInput>) => void;
  bad: (field: string) => boolean;
}) {
  const { t } = useTranslation();
  const on = useFeature("vehicleDriver");
  if (!on) return null;
  return (
    <fieldset className="driver-fields wide" aria-label={t("driver.title")}>
      <legend>
        {t("driver.title")} <Help id="driver.section" />
      </legend>
      <p className="driver-note wide">{t("driver.note")}</p>
      {stored && hasDriver(stored) && (
        <div className="wide">
          <CustomerContactLinks name={stored.driverName ?? null} mobile={stored.driverMobile} email={stored.driverEmail} />
        </div>
      )}
      <TextInput label={t("driver.mobile")} type="tel" value={form.driverMobile} onChange={(v) => onChange({ driverMobile: v })} invalid={bad("driverMobile")} />
      <TextInput label={t("driver.name")} value={form.driverName} onChange={(v) => onChange({ driverName: v })} />
      <TextInput label={t("driver.email")} type="email" value={form.driverEmail} onChange={(v) => onChange({ driverEmail: v })} invalid={bad("driverEmail")} />
    </fieldset>
  );
}

/**
 * On the open work order (owner approved, 2026-11-05): the customer's and the driver's phone side by side, each a
 * tel: link with its own label, for everyone who sees the customer (the mechanic too). Only with the option on and a
 * driver phone on the vehicle.
 */
export function WorkOrderPhones({ customer, vehicle }: { customer: Customer | null; vehicle: Vehicle | null }) {
  const { t } = useTranslation();
  const on = useFeature("vehicleDriver");
  const driverPhone = vehicle?.driverMobile ? normalizePhone(vehicle.driverMobile) : null;
  if (!on || !driverPhone) return null;
  const customerRaw = customer?.mobile || customer?.phone || "";
  const customerPhone = customerRaw ? normalizePhone(customerRaw) : null;
  return (
    <div className="wo-phones no-print" role="group" aria-label={t("driver.phones")}>
      {customerPhone && (
        <a className="button wo-phone" href={`tel:+${customerPhone}`}>
          <span className="wp-label">{t("driver.recipientCustomer")}</span>
          <span className="wp-value">{customerRaw}</span>
        </a>
      )}
      <a className="button wo-phone" href={`tel:+${driverPhone}`}>
        <span className="wp-label">{t("driver.label")}{vehicle?.driverName ? `: ${vehicle.driverName}` : ""}</span>
        <span className="wp-value">{vehicle!.driverMobile}</span>
      </a>
    </div>
  );
}
